//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import styled from '@emotion/styled';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter, setParams } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters, selectTaskFilterResolved } from '@/redux/selectors/dataTableSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import FilterChip from '@/components/_common/Filters/FilterChip';
import FilterSwitch from '@/components/_common/Filters/FilterSwitch';

export interface FilterBooleanProps {
  label: string;
  type: string;
  role?: ReturnType<typeof createSelector>;
  isQuickFilters: boolean;
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
}

const FilterBoolean = ({ label, type, role, isQuickFilters, advanceFilters, onAdvanceFiltersChange }: FilterBooleanProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const hasPermissions = role ? useAppSelector(role) : true;
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const isShowingResolved = useAppSelector(selectTaskFilterResolved);
  const isAdvanceFilter = isFiltersV2 && !isQuickFilters;
  const isSelected = type == 'show_resolved' ? isShowingResolved : isAdvanceFilter ? advanceFilters?.[type] : filters[type];

  const handleClick = () => {
    const selections = !isSelected;
    if (type === 'show_resolved') {
      if (!isSelected) {
        dispatch(setParams({ status_filter: ['in_error', 'resolved', 'failed'] }));
      } else {
        dispatch(setParams({ status_filter: ['in_error', 'failed'] }));
      }
    } else {
      const newValue = { type, selections };
      if (isAdvanceFilter && onAdvanceFiltersChange) {
        onAdvanceFiltersChange(newValue);
      } else {
        dispatch(applyFilter(newValue));
      }
    }
  };

  if (!hasPermissions) return null;

  return isFiltersV2 && !isQuickFilters ? (
    <FilterSwitchWrapper>
      <FilterSwitchLabel>{label}</FilterSwitchLabel>
      <FilterSwitch checked={isSelected} onChange={handleClick} />
    </FilterSwitchWrapper>
  ) : (
    <div style={{ marginRight: 10 }}>
      <FilterChip
        label={
          <>
            {isSelected && <PolarisIcon icon="check" height={17} style={{ marginRight: 5 }} />}
            <strong style={!isSelected ? { marginLeft: 0 } : {}}>{label}</strong>
          </>
        }
        isSelected={isSelected}
        onClick={handleClick}
      />
    </div>
  );
};

export default FilterBoolean;

const FilterSwitchWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  borderBottom: `2px solid ${theme.palette.background.default}`
}));

const FilterSwitchLabel = styled.p(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(16)
}));
